import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');
dayjs.extend(utc);
dayjs.extend(timezone);

export const date = (dt?: string | Date) =>
  dt ? dayjs(dt).tz('America/Sao_Paulo') : dayjs().tz('America/Sao_Paulo');
export const localeFormat = 'DD/MM/YYYY HH:mm:ss';

/**
 * Extrai ano/mês/dia de uma data-só-data ("YYYY-MM-DD", com ou sem hora/timezone
 * grudada) sem passar por `new Date(...)` — isso importa porque `new Date("2026-08-14")`
 * é sempre interpretado como UTC pelo spec, e formatar esse valor de volta com
 * `toLocaleDateString`/fuso local (Brasil = UTC-3) pode voltar um dia (23:00→dia
 * anterior). Todo campo "data de entrega" do sistema é data pura, sem hora — então
 * usar essas funções em vez de `new Date(x).toLocaleDateString()` evita esse -1 dia.
 */
function dateOnlyParts(value: string): [number, number, number] {
  const parts = date(value).format('YYYY-MM-DD').split('-');
  return [Number(parts[0]), Number(parts[1]), Number(parts[2])];
}

/** Formata uma data-só-data como "DD/MM/YYYY", sem nenhuma conversão de fuso horário. */
export function formatDateOnly(value?: string | null): string {
  if (!value) return '—';
  const [y, m, d] = dateOnlyParts(value);
  if (!y || !m || !d) return '—';
  return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

/** Diferença em dias de calendário entre hoje (fuso local de quem roda) e `value`. */
export function daysUntilDate(value: string): number {
  const [y, m, d] = dateOnlyParts(value);
  const target = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86400000);
}

/**
 * Normaliza uma data-só-data pra meio-dia (`YYYY-MM-DDT12:00:00`) antes de gravar num
 * campo `timestamp`. Meio-dia dá margem de ±12h pra qualquer fuso horário sem cruzar
 * pro dia errado, evitando o bug de "-1 dia" descrito em `dateOnlyParts`.
 */
export function toNoonTimestamp(value: string): string {
  const [y, m, d] = dateOnlyParts(value);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}T12:00:00`;
}
